import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Empty, Input, Space, Table, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { asyncSimpleModal } from '../../../lib/common';
import { SynonymLog, SynonymLogOperation, SynonymRollbackTarget } from '../../../lib/api/searchDictionary';
import { formatLogDate } from '../../pgProviderRoute/utils/formatLogDate';
import { searchDictionaryQuery } from '../queries';
import { useRollbackSynonymMutation } from '../hooks/useSynonymMutations';
import { readApiError } from '../utils/apiError';
import { formatTerms } from '../utils/terms';

export const SYNONYM_LOG_LIMIT = 50;

const OPERATION_LABEL: Record<SynonymLogOperation, { label: string; color: string }> = {
  create: { label: '추가', color: 'green' },
  update: { label: '수정', color: 'geekblue' },
  delete: { label: '삭제', color: 'red' },
  rollback: { label: '되돌림', color: 'purple' },
};

type SynonymHistoryPanelProps = {
  /** 이 규칙의 이력만 — 상세 페이지 */
  ruleId?: string;
  /** 되돌리기는 결과가 줄 생성·삭제가 될 수 있어 수정·등록·삭제 권한을 모두 요구한다 */
  canRollback: boolean;
  onOpenRule?: (ruleId: string) => void;
};

/** 변경 전후를 「+ 추가 / 빠짐」 으로 — diff 는 서버가 저장하지 않고 화면이 계산한다 */
const renderDiff = (log: SynonymLog): React.ReactNode => {
  const before = log.before ?? [];
  const after = log.after ?? [];
  const lower = (list: string[]): Set<string> => new Set(list.map((term) => term.toLowerCase()));
  const added = after.filter((term) => !lower(before).has(term.toLowerCase()));
  const removed = before.filter((term) => !lower(after).has(term.toLowerCase()));
  if (!before.length) return <span>{formatTerms(after)}</span>;
  if (!after.length) return <span className="diff-removed">{formatTerms(before)}</span>;
  return (
    <Space size={4} wrap>
      {added.map((term) => (
        <span key={`a-${term}`} className="diff-added">
          + {term}
        </span>
      ))}
      {removed.map((term) => (
        <span key={`r-${term}`} className="diff-removed">
          {term}
        </span>
      ))}
      {!added.length && !removed.length && <span className="settings-muted">순서·표기만 바뀜</span>}
    </Space>
  );
};

/**
 * 동의어 변경 이력 · 되돌리기 (D-①-4).
 * 「이 시점으로」 = 그 변경 **직후** 상태로, 「이 변경 전으로」 = 그 변경 **직전** 상태로.
 * 시드 줄(945줄)은 생성 기록이 없어서, 첫 수정·삭제 실수는 「이 변경 전으로」 로만 되돌릴 수 있다.
 */
export const SynonymHistoryPanel = (props: SynonymHistoryPanelProps): React.JSX.Element => {
  const { ruleId, canRollback, onOpenRule } = props;
  const { message } = App.useApp();
  const [termDraft, setTermDraft] = useState('');
  const [adminDraft, setAdminDraft] = useState('');
  const [filter, setFilter] = useState<{ term?: string; adminName?: string }>({});

  const query = useQuery({
    ...searchDictionaryQuery.logs({ ruleId, ...filter, limit: SYNONYM_LOG_LIMIT }),
    refetchOnMount: 'always',
  });
  const rollback = useRollbackSynonymMutation();

  const handleRollback = async (log: SynonymLog, to: SynonymRollbackTarget): Promise<void> => {
    const target = to === 'after' ? log.after : log.before;
    const isConfirmed = await asyncSimpleModal({
      type: 'confirm',
      title: to === 'after' ? '이 시점으로 되돌릴까요?' : '이 변경 전으로 되돌릴까요?',
      message: target
        ? `「${formatTerms(target)}」 으로 다시 저장합니다.\n동의어라 바로 검색에 반영됩니다.`
        : '이 줄을 지웁니다.\n동의어라 바로 검색에 반영됩니다.',
    });
    if (!isConfirmed) return;
    try {
      const response = await rollback.mutateAsync({ logId: log.id, ruleId: log.ruleId, to });
      void message.success(response.changed ? '되돌렸습니다. 바로 반영됐습니다.' : '이미 그 상태입니다.');
    } catch (error) {
      void message.error(readApiError(error, '되돌리지 못했습니다.').message);
    }
  };

  const columns: ColumnsType<SynonymLog> = [
    { title: '변경 일시', dataIndex: 'createdAt', width: 150, render: (date: string) => formatLogDate(date) },
    {
      title: '수정자',
      dataIndex: 'adminName',
      width: 100,
      render: (_, log) => log.adminName ?? (log.adminId ? `관리자 #${log.adminId}` : '시스템'),
    },
    {
      title: '구분',
      dataIndex: 'operation',
      width: 80,
      align: 'center',
      render: (operation: SynonymLogOperation) => (
        <Tag color={OPERATION_LABEL[operation].color}>{OPERATION_LABEL[operation].label}</Tag>
      ),
    },
    ...(ruleId
      ? []
      : [
          {
            title: '규칙',
            dataIndex: 'ruleId',
            width: 150,
            render: (id: string) =>
              onOpenRule ? (
                <Button type="link" size="small" className="rule-id" onClick={() => onOpenRule(id)}>
                  {id}
                </Button>
              ) : (
                <span className="rule-id">{id}</span>
              ),
          },
        ]),
    { title: '바뀐 것', render: (_, log) => renderDiff(log) },
    ...(canRollback
      ? [
          {
            title: '되돌리기',
            width: 200,
            render: (_: unknown, log: SynonymLog) => (
              <Space size={4}>
                <Button size="small" onClick={() => void handleRollback(log, 'after')}>
                  이 시점으로
                </Button>
                {log.before && (
                  <Button size="small" onClick={() => void handleRollback(log, 'before')}>
                    이 변경 전으로
                  </Button>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="settings-stack">
      {!ruleId && (
        <Alert
          className="settings-inline-alert"
          type="info"
          showIcon
          message="동의어는 저장할 때마다 기록됩니다. 되돌리기도 바로 반영되고, 되돌린 것도 기록됩니다."
          description="사용자사전 변경 이력은 레포 기록으로 봅니다."
        />
      )}
      <div className="settings-panel-header">
        {!ruleId ? (
          <Space wrap>
            <Input
              aria-label="토큰으로 찾기"
              placeholder="토큰 (예: 시그넷)"
              value={termDraft}
              onChange={(event) => setTermDraft(event.target.value)}
              allowClear
              style={{ width: 200 }}
            />
            <Input
              aria-label="수정자 이름으로 찾기"
              placeholder="수정자 이름"
              value={adminDraft}
              onChange={(event) => setAdminDraft(event.target.value)}
              allowClear
              style={{ width: 160 }}
            />
            <Button
              type="primary"
              onClick={() =>
                setFilter({ term: termDraft.trim() || undefined, adminName: adminDraft.trim() || undefined })
              }
            >
              검색
            </Button>
          </Space>
        ) : (
          <p>최신 {SYNONYM_LOG_LIMIT}건 · 저장 직후에는 이력 반영까지 잠시 걸릴 수 있습니다.</p>
        )}
        <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => query.refetch()}>
          새로고침
        </Button>
      </div>
      {query.isError ? (
        <Alert type="error" showIcon message="변경 이력을 불러오지 못했습니다. 새로고침해 주세요." />
      ) : (
        <Table
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={query.data?.logs ?? []}
          loading={query.isLoading}
          pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
          scroll={{ x: 760 }}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="표시할 변경 이력이 없습니다." />,
          }}
        />
      )}
    </div>
  );
};
